import { FsMatchTrack } from "db/model/match/Track/FsMatchTrack";
import { getDefinedRosterIds, Roster } from "model/Roster/Roster";
import { nonUndefined } from "Utility";
import { mapWorldTourRounds } from "./FsWorldTourRound";
import { WorldTourScoring } from "model/Match/Set/WorldTour/Format/WorldTourScoring";
import { WorldTourTrack } from "model/Match/Set/WorldTour/Track/WorldTourTrack";
import { Scores } from "model/Scores";
import { WorldTourRoundResult } from "model/Match/Set/WorldTour/Track/WorldTourRoundResult";
import { calculateFinalPositions } from "model/Positions";
import { mapMatchTrackBase } from "../../Track/FsMatchTrack";
import { mapTrackRounds } from "../../Track/FsTrackRound";
import { RosterScope } from "model/Roster/RosterScope";

export const mapWorldTourTracks = (
  fsModels: FsMatchTrack[],
  rosters: Roster[],
  rosterScope: RosterScope,
  scoring: WorldTourScoring,
  trackWinReq: number,
  driversPerTeam: number
) => {
  return nonUndefined(
    fsModels.map((t) =>
      mapWorldTourTrack(
        t,
        rosters,
        rosterScope,
        scoring,
        trackWinReq,
        driversPerTeam
      )
    )
  );
};

const mapWorldTourTrack = (
  fsModel: FsMatchTrack,
  rosters: Roster[],
  rosterScope: RosterScope,
  scoring: WorldTourScoring,
  winReq: number,
  driversPerTeam: number
): WorldTourTrack | undefined => {
  const baseRounds = mapTrackRounds(nonUndefined(fsModel.rounds ?? []));
  const rounds = mapWorldTourRounds(
    baseRounds,
    rosters,
    rosterScope,
    scoring,
    driversPerTeam
  );
  const scores = calculateTrackScores(
    rounds.flatMap((r) => r.results),
    fsModel.scoreOverrides,
    getDefinedRosterIds(rosters)
  );

  const isComplete =
    fsModel.overrideComplete || calculateIsComplete(scores, winReq);

  return {
    ...mapMatchTrackBase(fsModel),
    finalPositions: calculateFinalPositions(isComplete, scores, compareScores),
    isComplete,
    rounds,
    scores,
  };
};

const calculateTrackScores = (
  results: WorldTourRoundResult[],
  scoreOverrides: Scores | undefined,
  rosterIds: string[]
) => {
  const scores: Scores = {};
  for (var i = 0; i < rosterIds.length; i++) {
    const rosterId = rosterIds[i];
    const scoreOverride = !!scoreOverrides
      ? scoreOverrides[rosterId]
      : undefined;
    if (scoreOverride !== undefined) {
      scores[rosterId] = scoreOverride;
    } else {
      const rosterResults = results.filter((r) => r.rosterId === rosterId);
      scores[rosterId] = rosterResults.reduce((a, b) => a + b.points, 0);
    }
  }

  return scores;
};

const calculateIsComplete = (scores: Scores, winReq: number) => {
  const scoreValues = Object.values(scores);
  const bestScore = Math.max(...scoreValues, 0);
  if (bestScore < winReq) {
    return false;
  }

  return scoreValues.filter((s) => s === bestScore).length === 1;
};

const compareScores = (scores: Scores, a: string, b: string) => {
  return scores[b] - scores[a];
};
